import { useCallback, useMemo } from 'react'
import { FaChevronDown as CollapseIcon } from 'react-icons/fa6'
import { useNavigate } from '@tanstack/react-router'
import {
  useSequencerStore,
  selectCurrentProject,
  selectCurrentBuses,
  PADS_PER_PROJECT,
} from '@/stores/sequencerStore'
import { useInspectorStore } from '@/stores/inspectorStore'
import { resolvePad } from '@/lib/resolvePad'
import { KITS } from '@/hooks/useAudioEngine'
import {
  getInstrument,
  listInstrumentsByCategory,
} from '@/audio/plugins/registry'
import { defaultNoteForPad } from '@/lib/resolvePad'
import { usePluginEntitlementMap } from '@/hooks/usePluginEntitlement'
import { useAllSamples } from '@/hooks/useSampleUpload'
import { BusEffectChain } from './BusEffectChain'

/**
 * Bottom-docked Inspector panel — Ableton-style channel strip for
 * the Werkshop Sequencer surface.
 *
 * Selection-driven: when the user focuses a pad (via the grid or
 * PadEditor modal), the Inspector shows that pad's summary +
 * jumping-off actions. When nothing's selected, it shows project-
 * level state (BPM, open project name, master bus FX). A `pinned`
 * target on `inspectorStore` overrides the default focus-follow
 * behaviour for future "pin this bus" affordances.
 *
 * The full-fidelity pad editor still lives behind the modal
 * (double-click or right-click → Edit in modal) — the Inspector is
 * an at-a-glance surface that keeps the heavy editor one click away
 * without committing you to the modal on every interaction.
 */
export function Inspector() {
  const isOpen = useInspectorStore(s => s.isOpen)
  const height = useInspectorStore(s => s.height)
  const setHeight = useInspectorStore(s => s.setHeight)
  const setOpen = useInspectorStore(s => s.setOpen)
  const pinned = useInspectorStore(s => s.pinned)
  const clearPin = useInspectorStore(s => s.clearPin)

  const project = useSequencerStore(selectCurrentProject)
  const focusedPad = useSequencerStore(s => s.focusedPad)

  // Inferred target — pinned override wins, otherwise follow
  // sequencer focus (pad by default). Null-safe for pre-hydrate.
  const target = pinned ?? { kind: 'pad' as const, id: focusedPad }

  const handleDragStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const startY = e.clientY
      const startH = height
      const onMove = (mv: MouseEvent) => {
        setHeight(startH - (mv.clientY - startY))
      }
      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [height, setHeight],
  )

  if (!isOpen) return null

  const kit = project ? KITS[project.kitId] : undefined
  const resolved =
    project && kit && target.kind === 'pad'
      ? resolvePad(
          project,
          kit.voices.map(v => v),
          Number(target.id),
        )
      : null

  return (
    <aside
      role="complementary"
      aria-label="Inspector panel"
      className="fixed bottom-0 left-0 right-0 z-30 border-t border-brd-subtle bg-surface-sunken shadow-high flex flex-col"
      style={{ height: `${height}px` }}
    >
      {/* Drag handle + header */}
      <div
        className="h-2 cursor-row-resize bg-gradient-to-b from-transparent to-brd-subtle/20"
        onMouseDown={handleDragStart}
        aria-hidden="true"
      />
      <header className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-brd-subtle">
        <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
          Inspector ·{' '}
          <span className="text-content-secondary">
            {target.kind === 'pad'
              ? `Pad ${Number(target.id) + 1}`
              : target.kind === 'bus'
                ? `Bus ${target.id}`
                : 'Project'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {pinned && (
            <button
              type="button"
              onClick={clearPin}
              title="Clear pinned target — follow sequencer focus again"
              className="text-[10px] font-mono text-content-tertiary hover:text-content-primary px-2 py-0.5"
            >
              unpin
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            title="Hide Inspector"
            aria-label="Hide Inspector"
            className="p-1 rounded hover:bg-surface-raised text-content-secondary hover:text-content-primary"
          >
            <CollapseIcon size={12} />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-3 py-3">
        {target.kind === 'pad' && (
          <PadInspector resolvedLabel={resolved?.label ?? 'empty'} />
        )}
        {target.kind === 'bus' && <BusInspector busId={String(target.id)} />}
        {target.kind === 'project' && <ProjectInspector />}
      </div>
    </aside>
  )
}

function PadInspector({ resolvedLabel }: { resolvedLabel: string }) {
  const focusedPad = useSequencerStore(s => s.focusedPad)
  const pinTarget = useInspectorStore(s => s.pinTarget)
  const buses = useSequencerStore(selectCurrentBuses)
  const project = useSequencerStore(selectCurrentProject)
  const setPadContent = useSequencerStore(s => s.setPadContent)
  const padDef =
    focusedPad >= 0 && focusedPad < PADS_PER_PROJECT
      ? project?.padDefs?.[focusedPad]
      : undefined
  const { data: samples = [] } = useAllSamples()
  // Sampler-instrument pads (and future buffer-backed instruments)
  // store a `sampleId` that references the sample library; resolve
  // it so the Inspector can show the sample name + slice detail
  // without requiring users to open the modal PadEditor.
  const samplerMeta = useMemo(() => {
    if (!padDef) return null
    if (padDef.kind === 'instrument' && padDef.instrumentId === 'sampler') {
      const s = padDef.sampleId
        ? samples.find(x => x.id === padDef.sampleId)
        : undefined
      const slice =
        padDef.sliceId && s?.slices
          ? s.slices.find(x => x.id === padDef.sliceId)
          : undefined
      return { sample: s, slice }
    }
    return null
  }, [padDef, samples])

  // Registry plugin for the focused pad — only resolved when this
  // pad is a `kind: 'instrument'` pad pointing at a registered id.
  // Anything else (kit defaults, legacy sample / synth / midi pads)
  // renders the fallback copy.
  const plugin = useMemo(() => {
    if (!padDef || padDef.kind !== 'instrument') return null
    return getInstrument(padDef.instrumentId) ?? null
  }, [padDef])

  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-content-tertiary text-xs uppercase tracking-wider">
          Pad {focusedPad + 1}
        </span>
        <span className="text-content-primary font-mono text-xs truncate max-w-[40ch]">
          {resolvedLabel}
        </span>
      </div>

      {samplerMeta?.sample && (
        <div className="flex flex-col gap-0.5 text-xs border-l-2 border-accent/60 pl-2">
          <span className="text-content-tertiary text-[10px] font-mono uppercase tracking-wider">
            Sample
          </span>
          <span className="text-content-primary font-mono truncate">
            {samplerMeta.sample.name}
          </span>
          {samplerMeta.slice ? (
            <span className="text-content-secondary text-[10px] font-mono">
              slice {samplerMeta.slice.label ?? samplerMeta.slice.id} ·{' '}
              {samplerMeta.slice.startSec.toFixed(2)}s →{' '}
              {samplerMeta.slice.endSec.toFixed(2)}s
            </span>
          ) : (
            <span className="text-content-tertiary text-[10px] font-mono italic">
              full sample
            </span>
          )}
        </div>
      )}

      {plugin && padDef && padDef.kind === 'instrument' ? (
        <p className="text-xs text-content-secondary leading-relaxed">
          <span className="text-content-primary font-medium">
            {plugin.label}
          </span>{' '}
          — open the Cell Editor&apos;s <em>Pad</em> tab below to tweak
          parameters and routing. This pane stays lightweight for quick
          audition.
        </p>
      ) : (
        <PadInstrumentPicker focusedPad={focusedPad} />
      )}

      {buses.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-content-tertiary text-xs uppercase tracking-wider">
            Buses
          </span>
          <div className="flex flex-wrap gap-1.5">
            {buses.map(b => (
              <button
                key={b.id}
                type="button"
                onClick={() => pinTarget({ kind: 'bus', id: b.id })}
                className="px-2 py-0.5 rounded border border-brd-subtle text-xs font-mono text-content-secondary hover:border-accent hover:text-accent transition-colors"
              >
                {b.name ?? b.id}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Quick-convert row shown in the Pad Inspector when the focused
 * pad is NOT a registry-backed instrument (kit-default, synth,
 * sample, granular, midi). Presents a single-tap path to drop a
 * first-party instrument onto the pad so the rest of the Inspector
 * (param sliders etc.) becomes useful without opening the modal.
 *
 * Honours the same entitlement gates as the pad editor picker and
 * the Tools page — unowned modules route to the Store.
 */
function PadInstrumentPicker({ focusedPad }: { focusedPad: number }) {
  const setPadContent = useSequencerStore(s => s.setPadContent)
  const entitlementFor = usePluginEntitlementMap()
  const navigate = useNavigate()
  const groups = useMemo(() => listInstrumentsByCategory(), [])
  return (
    <div className="flex flex-col gap-2 text-xs">
      <p className="text-content-secondary leading-relaxed">
        Drop a first-party instrument onto this pad to get full parameter
        controls here. Long-press the pad or tap the Pads button for the full
        editor.
      </p>
      <div className="flex flex-col gap-1.5">
        {(['synth', 'sampler', 'hybrid'] as const).map(category => {
          const plugins = groups[category]
          if (!plugins?.length) return null
          return (
            <div key={category} className="flex flex-wrap items-center gap-1">
              <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-content-tertiary w-14">
                {category === 'sampler'
                  ? 'sampler'
                  : category === 'hybrid'
                    ? 'hybrid'
                    : 'synths'}
              </span>
              {plugins.map(plugin => {
                const entitlement = entitlementFor(plugin.id)
                const locked = entitlement === null
                return (
                  <button
                    key={plugin.id}
                    type="button"
                    onClick={() => {
                      if (locked) {
                        void navigate({
                          to: '/werkshop/store',
                          hash: plugin.id,
                        })
                        return
                      }
                      setPadContent(focusedPad, {
                        kind: 'instrument',
                        instrumentId: plugin.id,
                        label: plugin.label,
                        midiNote: defaultNoteForPad(focusedPad),
                        preset: {},
                        gainDb: 0,
                      })
                    }}
                    title={
                      locked
                        ? `${plugin.label} — get in Store`
                        : `Switch pad to ${plugin.label}`
                    }
                    className={`px-2 py-0.5 rounded border text-[10px] font-mono transition-colors ${
                      locked
                        ? 'border-brd-subtle text-content-tertiary hover:border-accent hover:text-accent'
                        : 'border-brd-subtle text-content-secondary hover:border-accent hover:text-accent'
                    }`}
                  >
                    {plugin.label}
                    {locked && <span className="ml-1 text-accent">🔒</span>}
                  </button>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function BusInspector({ busId }: { busId: string }) {
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="text-content-tertiary text-xs uppercase tracking-wider">
        Bus FX chain
      </div>
      <BusEffectChain busId={busId} />
    </div>
  )
}

function ProjectInspector() {
  const project = useSequencerStore(selectCurrentProject)
  const setBpm = useSequencerStore(s => s.setBpm)
  if (!project) {
    return <p className="text-sm text-content-secondary">No project open.</p>
  }
  return (
    <div className="flex flex-col gap-3 text-sm">
      <label className="flex flex-col gap-1 w-40">
        <span className="text-content-tertiary text-xs uppercase tracking-wider">
          BPM
        </span>
        <input
          type="number"
          min={30}
          max={300}
          step={0.1}
          value={project.bpm}
          onChange={e =>
            setBpm(Math.max(30, Math.min(300, Number(e.target.value) || 120)))
          }
          className="bg-surface-raised border border-brd-subtle rounded px-2 py-1 text-content-primary"
        />
      </label>
      <p className="text-xs text-content-secondary">
        Project-level: {project.name} — {project.stepCount} steps · kit{' '}
        {project.kitId}. Scrambler + master-FX editors live in the Project
        settings page.
      </p>
    </div>
  )
}

/**
 * Header pill that toggles the Inspector open/closed. Mount in
 * `WorkspaceHeader`'s rightExtras slot on Werkshop Sequencer
 * pages so users can summon/dismiss the panel.
 */
export function InspectorToggle() {
  const isOpen = useInspectorStore(s => s.isOpen)
  const toggle = useInspectorStore(s => s.toggle)
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={isOpen}
      title={`${isOpen ? 'Hide' : 'Show'} Inspector (I)`}
      className={`px-2 py-1 rounded border text-[10px] font-mono uppercase tracking-wider transition-colors ${
        isOpen
          ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
          : 'border-brd-subtle text-content-secondary hover:border-brd-emphasis'
      }`}
    >
      Inspector
    </button>
  )
}
